import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { NzIconModule } from 'ng-zorro-antd/icon';
import { BUCKET_META } from 'src/app/demo/packetBuckets/packet-bucket.models';
import { DOCUMENTS_RECEIVED_PATH, findDocumentsReceivedItem } from 'src/app/demo/documentsReceived/documents-received.models';
import { PageBreadcrumbService } from './page-breadcrumb.service';

interface PageMeta {
  title: string;
  icon: string;
}

@Component({
  selector: 'app-page-breadcrumb',
  imports: [NzIconModule, AsyncPipe],
  templateUrl: './page-breadcrumb.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./page-breadcrumb.component.scss']
})
export class PageBreadcrumbComponent implements OnInit, OnDestroy {
  meta: PageMeta | null = null;

  private static readonly routes: Record<string, PageMeta> = {
    '/dashboard':                    { title: 'Dashboard',               icon: 'dashboard'           },
    '/incoming-referral':            { title: 'Incoming Referrals',      icon: 'file-done'           },
    '/web-referral':                 { title: 'Web Referral',            icon: 'form'                },
    '/build-referral':               { title: 'Build Referral',          icon: 'build'               },
    '/workflow-summary':             { title: 'Workflow Summary',        icon: 'appstore'            },
    '/exception-queue':              { title: 'Exception Queue',         icon: 'alert'               },
    '/non-referrals':                { title: 'Non-Referrals',           icon: 'file-unknown'        },
    '/user-list':                    { title: 'User List',               icon: 'team'                },
    '/miscellanous-page':            { title: 'Miscellaneous Files',     icon: 'folder-open'         },
    '/acc-info':                     { title: 'Account Info',            icon: 'info-circle'         },
    '/pageException':                { title: 'Page Exception',          icon: 'exclamation-circle'  },
    '/BotQueue/Index':               { title: 'Files Received',          icon: 'file-text'           },
    '/BotQueue/Details':             { title: 'File Details',            icon: 'profile'             },
    '/BotQueue/ExceptionDetails':    { title: 'Exception Details',       icon: 'file-search'         },
    '/BotQueue/ExceptionPageDetails':{ title: 'Exception Page Details',  icon: 'file-search'         },
    '/dashboard-analytics':          { title: 'Dashboard Analytics',     icon: 'area-chart'          },
    '/page-entities-admin':          { title: 'Page Entities',     icon: 'form'                },
    '/page-layout':                  { title: 'Page Layout',             icon: 'appstore'            },
    '/tenant-config':                { title: 'Tenant Config',           icon: 'setting' ,            },
    '/keyword-management':           { title: 'Document Creation',       icon: 'file-text'           }
  };

  private static readonly prefixes: Array<{ prefix: string; meta: PageMeta }> = [
    { prefix: '/referral-details/', meta: { title: 'Referral Details', icon: 'solution' } },
    { prefix: '/web-referral/', meta: { title: 'Web Referral', icon: 'form' } }
  ];

  private sub?: Subscription;

  private readonly breadcrumbService = inject(PageBreadcrumbService);
  readonly badge$ = this.breadcrumbService.badge$;

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.update(this.router.url);
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        // A page-supplied pill belongs to that page only — drop it when the route changes.
        this.breadcrumbService.clearBadge();
        this.update(e.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private update(url: string): void {
    const path = url.split('?')[0];

    // Packet Buckets submenu items reuse the Incoming Referrals page with ?bucket=<name> — show
    // that bucket's own title/icon (from the same BUCKET_META the nav submenu badges use) instead
    // of the generic "Incoming Referrals" heading.
    if (path === '/incoming-referral') {
      const bucket = this.router.parseUrl(url).queryParams['bucket'];
      if (bucket) {
        this.meta = { title: bucket, icon: BUCKET_META[bucket]?.icon ?? 'folder-open' };
        return;
      }
    }

    // Documents Received submenu items share one page, told apart by ?status= — name the item in the
    // heading, with its own submenu icon.
    if (path === DOCUMENTS_RECEIVED_PATH) {
      const item = findDocumentsReceivedItem(this.router.parseUrl(url).queryParams['status']);
      this.meta = { title: `Documents Received · ${item.title}`, icon: item.icon };
      return;
    }

    const exact = PageBreadcrumbComponent.routes[path];
    if (exact) {
      this.meta = exact;
      return;
    }
    const prefix = PageBreadcrumbComponent.prefixes.find((p) => path.startsWith(p.prefix));
    this.meta = prefix?.meta ?? null;
  }
}
